import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Mail, Send, Trash2, RefreshCw, X, ShieldCheck, Loader2, CheckCircle2, Clock3,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  grantClientAccess, listClientAccessGrants, resendClientAccess, revokeClientAccess,
} from "../lib/clientAccess";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const STATE_META = {
  active: { label: "Active", bg: "#DCFCE7", fg: "#166534", Icon: CheckCircle2 },
  pending: { label: "Invited", bg: "#FEF3C7", fg: "#92400E", Icon: Clock3 },
  revoked: { label: "Revoked", bg: "#FEE2E2", fg: "#991B1B", Icon: X },
};

/**
 * Manage Client Access — consultant/admin dialog to invite, view, resend and
 * revoke read-only client access on a FINALIZED assignment. Module-agnostic
 * (works for SmartEvaluate, SmartImprove, and future modules).
 */
export default function ManageClientAccessDialog({
  open, onOpenChange, module, assignmentId, assignmentTitle,
}) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState("");

  const refresh = useCallback(async () => {
    if (!module || !assignmentId) return;
    setLoading(true);
    try {
      setGrants(await listClientAccessGrants(module, assignmentId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load client access");
    } finally {
      setLoading(false);
    }
  }, [module, assignmentId]);

  useEffect(() => {
    if (open) {
      setEmails([]); setEmailInput(""); setMessage(""); setEmailError("");
      refresh();
    }
  }, [open, refresh]);

  const commitEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (!v) return;
    if (!EMAIL_RE.test(v)) { setEmailError(`Invalid email: ${v}`); return; }
    if (!emails.includes(v)) setEmails((p) => [...p, v]);
    setEmailInput(""); setEmailError("");
  };

  const onEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault(); commitEmail();
    }
  };

  const sendInvites = async () => {
    const list = [...emails];
    const pending = emailInput.trim().toLowerCase();
    if (pending) {
      if (!EMAIL_RE.test(pending)) { setEmailError(`Invalid email: ${pending}`); return; }
      if (!list.includes(pending)) list.push(pending);
    }
    if (list.length === 0) { setEmailError("Add at least one email"); return; }
    setSending(true);
    try {
      const res = await grantClientAccess(module, assignmentId, list, message);
      const invited = (res.results || []).filter((r) => r.status === "invited").length;
      const already = (res.results || []).filter((r) => r.status === "already_has_access").length;
      toast.success(
        `${invited} invitation${invited === 1 ? "" : "s"} sent${already ? ` · ${already} already had access` : ""}`
      );
      setEmails([]); setEmailInput(""); setMessage("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invitations");
    } finally {
      setSending(false);
    }
  };

  const onResend = async (g) => {
    setBusyId(g.grant_id);
    try {
      await resendClientAccess(g.grant_id);
      toast.success(`Invitation resent to ${g.email}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally { setBusyId(""); }
  };

  const onRevoke = async (g) => {
    setBusyId(g.grant_id);
    try {
      await revokeClientAccess(g.grant_id);
      toast.success(`Access revoked for ${g.email}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    } finally { setBusyId(""); }
  };

  const activeGrants = grants.filter((g) => g.status === "active");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl rounded-sm"
        data-testid="manage-client-access-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <ShieldCheck className="w-5 h-5 text-[#025979]" />
            Manage Client Access
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Give clients secure, read-only access to{" "}
            <span className="font-semibold text-[#0F172A]">{assignmentTitle}</span>.
            They can view the finalized assignment but cannot make changes.
          </DialogDescription>
        </DialogHeader>

        {/* Invite form */}
        <div className="space-y-2.5">
          <label className="text-xs font-semibold text-[#374151] flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Invite by email
          </label>
          <div className="flex flex-wrap items-center gap-1.5 border border-[#E5E7EB] rounded-sm px-2 py-1.5 min-h-[40px]">
            {emails.map((em) => (
              <span
                key={em}
                className="inline-flex items-center gap-1 bg-[#E0F2FE] text-[#025979] text-xs font-medium rounded-sm px-2 py-0.5"
                data-testid={`invite-chip-${em}`}
              >
                {em}
                <button type="button" onClick={() => setEmails((p) => p.filter((x) => x !== em))} aria-label={`Remove ${em}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              data-testid="invite-email-input"
              value={emailInput}
              onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
              onKeyDown={onEmailKeyDown}
              onBlur={commitEmail}
              placeholder="client@company.com"
              className="flex-1 min-w-[160px] text-sm outline-none bg-transparent"
            />
          </div>
          {emailError && (
            <p className="text-xs text-red-500" data-testid="invite-email-error">{emailError}</p>
          )}
          <Textarea
            data-testid="invite-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional message to include in the invitation email"
            className="rounded-sm text-sm min-h-[60px]"
          />
          <Button
            data-testid="invite-send-btn"
            onClick={sendInvites}
            disabled={sending}
            className="w-full bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-9"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send Invitation{emails.length > 1 ? "s" : ""}
          </Button>
        </div>

        {/* Invited users list */}
        <div className="mt-2 border-t border-[#E5E7EB] pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#374151] flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Invited clients
              <span className="text-[#94A3B8] font-normal">({activeGrants.length} active)</span>
            </p>
            <button
              type="button"
              onClick={refresh}
              className="text-[#64748B] hover:text-[#025979]"
              aria-label="Refresh"
              data-testid="client-access-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {grants.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-3 text-center" data-testid="client-access-empty">
              No clients have been invited yet.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="client-access-list">
              {grants.map((g) => {
                const meta = STATE_META[g.user_state] || STATE_META.pending;
                const Icon = meta.Icon;
                return (
                  <li
                    key={g.grant_id}
                    className="flex items-center justify-between gap-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-2.5 py-1.5"
                    data-testid={`client-access-row-${g.email}`}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-[#0F172A] truncate">{g.email}</p>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-sm px-1.5 py-0.5 mt-0.5"
                        style={{ backgroundColor: meta.bg, color: meta.fg }}
                      >
                        <Icon className="w-2.5 h-2.5" /> {meta.label}
                      </span>
                    </div>
                    {g.status === "active" ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => onResend(g)}
                          disabled={busyId === g.grant_id}
                          className="h-7 px-2 text-[11px] rounded-sm"
                          data-testid={`client-access-resend-${g.email}`}
                        >
                          {busyId === g.grant_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          <span className="ml-1">Resend</span>
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => onRevoke(g)}
                          disabled={busyId === g.grant_id}
                          className="h-7 px-2 text-[11px] rounded-sm text-[#B91C1C] border-[#FECACA] hover:bg-[#FEE2E2]"
                          data-testid={`client-access-revoke-${g.email}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          <span className="ml-1">Revoke</span>
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#94A3B8] shrink-0">Revoked</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
